import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../api/authApi";
import Spinner from "../components/Spinner";

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    companyId: "",
    role: "User",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.email.includes("@")) return "Please enter a valid email address.";
    if (formData.password.length < 6) return "Password must be at least 6 characters.";
    if (!formData.companyId) return "Company ID is required.";
    return null;
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.register(formData);
      // Assuming the API returns the token for auto-login on success
      if (res.data.token) {
        login(res.data.token);
        navigate("/");
      } else {
        // If API doesn't return token, redirect to login
        navigate("/login");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || 
        (typeof err.response?.data === 'string' ? err.response.data : "Registration failed. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full max-w-lg p-8 card bg-white shadow-xl border border-slate-200 animate-fade-in my-10">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl mb-4 shadow-lg shadow-blue-200">
          <span className="text-white font-bold text-xl">S</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900">Create Account</h1>
        <p className="text-slate-500 mt-2">Join Strivre to manage your services</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="firstName">
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder="John"
              value={formData.firstName}
              onChange={handleChange}
              className="input-base w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="lastName">
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              placeholder="Doe"
              value={formData.lastName}
              onChange={handleChange}
              className="input-base w-full"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="email">
            Email Address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="name@company.com"
            value={formData.email}
            onChange={handleChange}
            className="input-base w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            placeholder="••••••••"
            value={formData.password}
            onChange={handleChange}
            className="input-base w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="companyId">
            Company ID (Guid)
          </label>
          <input
            id="companyId"
            name="companyId"
            type="text"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={formData.companyId}
            onChange={handleChange}
            className="input-base w-full"
            required
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg animate-shake">
            {error}
          </div>
        )}

        <button 
          type="submit" 
          disabled={isLoading}
          className="btn-primary w-full py-3 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="h-5 w-5" />
              Creating Account...
            </>
          ) : "Register"}
        </button>
      </form>

      <div className="mt-8 text-center border-t border-slate-100 pt-6">
        <p className="text-slate-600 text-sm">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 font-bold hover:underline">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}